import React, { useEffect, useMemo } from 'react';
import AppSelect from './AppSelect';
import useRemoteClientOptions from '../../hooks/useRemoteClientOptions';

const optionText = (o) => (o.mobile ? `${o.label} (${o.mobile})` : o.label);

/**
 * انتخاب مشتری با جستجو و صفحه‌بندی سمت سرور (clients/options).
 * جایگزین Select هایی که کل لیست مشتریان را از index/create/edit می‌گرفتند.
 *
 * props:
 *  - value / onChange: مثل antd Select (داخل Form.Item هم کار می‌کند)
 *  - leadingOptions: گزینه‌های ثابت بالای لیست، مثل [{ value: 'all', label: 'همه' }]
 *  - knownOptions: مشتری‌هایی که از قبل معلوم‌اند ({ value, label }) تا درخواست اضافه زده نشود
 *  - includeInactive: نمایش مشتریان غیرفعال
 *  - onClientSelect(option | option[] | null): کل اطلاعات مشتری انتخاب‌شده (name, mobile, email, company_name)
 */
const ClientSelect = React.forwardRef(
  (
    {
      value,
      onChange,
      leadingOptions = [],
      knownOptions,
      includeInactive = false,
      onClientSelect,
      placeholder = 'جستجوی نام یا موبایل مشتری',
      style,
      ...rest
    },
    ref,
  ) => {
    const numericSelected = useMemo(
      () => [].concat(value ?? []).filter((v) => v !== 'all' && v !== '' && v != null),
      [value],
    );
    const remote = useRemoteClientOptions({ selectedValue: numericSelected, includeInactive });
    const { addOptions } = remote;

    // کلید پایدار تا آرایه‌ی inline در هر رندر باعث setState تکراری نشود
    const knownKey = JSON.stringify(
      (knownOptions || []).map((o) => [Number(o.value), o.label, o.mobile || '']),
    );
    useEffect(() => {
      const list = JSON.parse(knownKey).map(([v, label, mobile]) => ({ value: v, label, mobile }));
      if (list.length) addOptions(list);
    }, [knownKey, addOptions]);

    const handleChange = (val, opt) => {
      onChange?.(val, opt);
      if (!onClientSelect) return;
      const pick = (v) => remote.options.find((o) => o.value === Number(v)) || null;
      onClientSelect(Array.isArray(val) ? val.map(pick).filter(Boolean) : val == null ? null : pick(val));
    };

    const normalizedValue = Array.isArray(value)
      ? value.map((v) => (v === 'all' ? v : Number(v)))
      : value === '' || value == null
        ? undefined
        : value === 'all'
          ? 'all'
          : Number(value);

    return (
      <AppSelect
        ref={ref}
        value={normalizedValue}
        onChange={handleChange}
        placeholder={placeholder}
        allowClear
        style={{ width: '100%', fontFamily: 'CustomFont', ...style }}
        {...remote.selectProps}
        notFoundContent={remote.loading ? 'در حال جستجو...' : 'مشتری یافت نشد'}
        options={[
          ...leadingOptions,
          ...remote.options.map((o) => ({ value: o.value, label: optionText(o) })),
        ]}
        {...rest}
      />
    );
  },
);

ClientSelect.displayName = 'ClientSelect';

export default ClientSelect;
